<template>
  <section id="batch-transfer-table">
    <b-table
      :data="items"
      :mobile-cards="false"
      narrowed
      sticky-header
      :row-class="rowClass"
      :height="height"
    >
      <b-table-column label="" width="40" centered v-slot="props">
        <b-tooltip v-show="props.row.error" position="is-right">
          <b-icon icon="information" type="is-danger"></b-icon>
          <template v-slot:content>
            {{ props.row.error }}
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.group_code')"
        width="100"
        centered
        sortable
        v-slot="props"
      >
        <b-tooltip type="is-light" position="is-right">
          <p>{{ props.row.code }}</p>
          <template v-slot:content>
            <p v-html="props.row.name"></p>
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="departure_date"
        :label="$t('attributes.departure_date')"
        width="140"
        sortable
        centered
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ formatDate(props.row.departure_date) }}
      </b-table-column>

      <b-table-column
        field="days"
        :label="$t('attributes.days')"
        width="80"
        centered
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.days }}
      </b-table-column>

      <b-table-column label="航班" centered v-slot="props">
        <b-table
          :data="props.row.transfers"
          :mobile-cards="false"
          narrowed
          hoverable
          :class="`transfer-table ` + rowClass(props.row, props.index)"
          :row-class="row => transferRowClass(props.row, props.index, row)"
        >
          <b-table-column
            field="day"
            width="100"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-field
              :label="transfers.index === 0 ? $t('attributes.day') : ''"
              label-position="on-border"
            >
              <b-select
                v-model="transfers.row.day"
                name="day"
                expanded
                @input="modified(props.row)"
              >
                <option
                  v-for="option in props.row.dayOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </b-field>
          </b-table-column>

          <b-table-column
            field="sort"
            width="100"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-field
              :label="transfers.index === 0 ? $t('attributes.day_sort') : ''"
              label-position="on-border"
            >
              <b-select
                v-model="transfers.row.sort"
                name="sort"
                expanded
                @input="modified(props.row)"
              >
                <option
                  v-for="option in sortOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </b-field>
          </b-table-column>

          <b-table-column
            field="direction"
            width="100"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-field
              :label="
                transfers.index === 0 ? $t('attributes.traffic_direction') : ''
              "
              label-position="on-border"
            >
              <b-select
                v-model="transfers.row.direction"
                name="direction"
                expanded
                @input="modified(props.row)"
              >
                <option value="去">去</option>
                <option value="中">中</option>
                <option value="回">回</option>
              </b-select>
            </b-field>
          </b-table-column>

          <b-table-column
            field="fly_no"
            width="120"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-field
              :label="transfers.index === 0 ? $t('attributes.fly_no') : ''"
              label-position="on-border"
            >
              <b-autocomplete
                v-model="transfers.row.scheduleValue"
                name="fly_no"
                field="fly_no"
                :data="transfers.row.schedules"
                :placeholder="
                  $t('message.please_select') + $t('attributes.fly_schedule')
                "
                :open-on-focus="true"
                :keep-first="true"
                :loading="loadingSchedules"
                :check-infinite-scroll="true"
                class="schedule-autocomplete"
                @typing="search => onSelectingSchedule(transfers.row, search)"
                @select="
                  option =>
                    onSelectedSchedule(props.row, transfers.index, option)
                "
              >
                <template #default="schedules">
                  <div class="media fly-schedule-media">
                    <div class="media-left">
                      <p class="has-text-black has-text-weight-semibold">
                        {{ schedules.option.airline }}
                      </p>
                      <p>
                        <span class="has-text-black has-text-weight-bold">
                          {{ schedules.option.fly_no }}
                        </span>
                        班機
                      </p>
                    </div>
                    <div class="media-content">
                      <p>
                        出發
                        <span class="has-text-info has-text-weight-semibold">
                          {{ schedules.option.departure_time }}
                          {{ schedules.option.departure_city }}
                        </span>
                      </p>
                      <p>
                        到達
                        <span class="has-text-success has-text-weight-semibold">
                          {{ schedules.option.arrive_time }}
                          {{ schedules.option.arrive_city }}
                        </span>
                      </p>
                    </div>
                    <div class="media-right">
                      <table class="table schedule-table">
                        <tbody>
                          <tr>
                            <td rowspan="2">
                              <span
                                :class="{
                                  'week-day': true,
                                  'week-check': schedules.option.mon
                                }"
                              >
                                一
                              </span>
                            </td>
                            <td rowspan="2">
                              <span
                                :class="{
                                  'week-day': true,
                                  'week-check': schedules.option.tue
                                }"
                              >
                                二
                              </span>
                            </td>
                            <td rowspan="2">
                              <span
                                :class="{
                                  'week-day': true,
                                  'week-check': schedules.option.wen
                                }"
                              >
                                三
                              </span>
                            </td>
                            <td rowspan="2">
                              <span
                                :class="{
                                  'week-day': true,
                                  'week-check': schedules.option.thr
                                }"
                              >
                                四
                              </span>
                            </td>
                            <td rowspan="2">
                              <span
                                :class="{
                                  'week-day': true,
                                  'week-check': schedules.option.fri
                                }"
                              >
                                五
                              </span>
                            </td>
                            <td rowspan="2">
                              <span
                                :class="{
                                  'week-day': true,
                                  'week-check': schedules.option.sat
                                }"
                              >
                                六
                              </span>
                            </td>
                            <td rowspan="2">
                              <span
                                :class="{
                                  'week-day': true,
                                  'week-check': schedules.option.sun
                                }"
                              >
                                日
                              </span>
                            </td>
                            <td style="padding-left: 10px">生效時間</td>
                            <td
                              class="has-text-danger has-text-weight-semibold"
                            >
                              {{ schedules.option.effect_date }}
                            </td>
                          </tr>
                          <tr>
                            <td style="padding-left: 10px">預估天數</td>
                            <td
                              class="has-text-primary has-text-weight-semibold"
                            >
                              {{ schedules.option.estimated_days }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </template>
                <template #empty>
                  No results found
                </template>
              </b-autocomplete>
            </b-field>
          </b-table-column>

          <b-table-column
            field="estimated_days"
            width="95"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-field
              :label="
                transfers.index === 0 ? $t('attributes.estimated_days') : ''
              "
              label-position="on-border"
            >
              <b-input
                v-model="transfers.row.estimated_days"
                type="number"
                readonly
              ></b-input>
            </b-field>
          </b-table-column>

          <b-table-column
            field="airline"
            width="150"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-field
              :label="transfers.index === 0 ? $t('attributes.airline') : ''"
              label-position="on-border"
            >
              <b-input v-model="transfers.row.airline" readonly></b-input>
            </b-field>
          </b-table-column>

          <b-table-column
            field="departure_time"
            width="100"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-field
              :label="
                transfers.index === 0 ? $t('attributes.departure_time') : ''
              "
              label-position="on-border"
            >
              <b-input
                v-model="transfers.row.departure_time"
                readonly
              ></b-input>
            </b-field>
          </b-table-column>

          <b-table-column
            field="departure_city"
            width="150"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-field
              :label="
                transfers.index === 0 ? $t('attributes.departure_city') : ''
              "
              label-position="on-border"
            >
              <b-input
                v-model="transfers.row.departure_city"
                readonly
              ></b-input>
            </b-field>
          </b-table-column>

          <b-table-column
            field="arrive_time"
            width="100"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-field
              :label="transfers.index === 0 ? $t('attributes.arrive_time') : ''"
              label-position="on-border"
            >
              <b-input v-model="transfers.row.arrive_time" readonly></b-input>
            </b-field>
          </b-table-column>

          <b-table-column
            field="arrive_city"
            width="150"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-field
              :label="transfers.index === 0 ? $t('attributes.arrive_city') : ''"
              label-position="on-border"
            >
              <b-input v-model="transfers.row.arrive_city" readonly></b-input>
            </b-field>
          </b-table-column>
          <b-table-column
            width="60"
            :header-class="rowClass(props.row, props.index)"
            v-slot="transfers"
          >
            <b-button
              type="is-danger"
              icon-left="delete"
              @click.prevent="onDeleteTransfer(props.row, transfers.index)"
            >
            </b-button>
          </b-table-column>
        </b-table>
      </b-table-column>

      <b-table-column
        width="160"
        :label="$t('attributes.function')"
        centered
        v-slot="props"
      >
        <div class="field is-grouped">
          <b-tooltip :label="$t('attributes.add') + $t('attributes.traffic')">
            <b-button
              type="is-primary"
              icon-left="plus"
              class="mr-1"
              @click.prevent="onAddTransfer(props.row)"
            >
            </b-button>
          </b-tooltip>
          <b-tooltip :label="$t('attributes.restore')">
            <b-button
              type="is-info"
              icon-left="restore"
              outlined
              class="ml-1"
              @click.prevent="restore(props.row)"
            >
            </b-button>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section" style="height: 100%">
          <div
            v-if="!isLoading"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-loading
      :is-full-page="false"
      :active.sync="loadingSchedules"
    ></b-loading>
  </section>
</template>

<script>
import { FLY_SCHEDULE_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import { debounce } from 'lodash'

export default {
  name: 'BatchTransferTable',
  props: {
    groups: {
      Type: Array,
      required: true
    },
    height: Number,
    loading: Boolean
  },
  data() {
    return {
      items: this.extractGroups(this.groups),

      sortOptions: [1, 2, 3, 4, 5],

      loadingSchedules: false,
      schedules: []
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.loadingSchedules
    }
  },
  watch: {
    groups(list) {
      this.items = this.extractGroups(list)
    }
  },
  mounted() {
    this.getSchedules()
  },
  methods: {
    async getSchedules() {
      const api = `${FLY_SCHEDULE_API}/list`
      const params = { search: null }

      try {
        this.loadingSchedules = true
        const res = await this.axios.get(api, { params })

        this.schedules = res.data.data.map(s => ({
          ...s,
          fly_no: s.fly_no.trim().toUpperCase()
        }))
        this.filteringSchedules()
      } catch (e) {
        this.schedules = []
        this.filteringSchedules()

        notifyService.fetch_error('fly_schedule', e)
      } finally {
        this.loadingSchedules = false
      }
    },
    extractGroups(groups) {
      return groups.map(group => {
        const transfers = this.extractTransfers(group.transfers)

        return {
          code: group.code,
          departure_date: group.departure_date,
          name: group.name,
          days: group.days,
          dayOptions: [...Array(group.days).keys()].map(i => i + 1),
          transfers: transfers,

          dirty: false,
          error: null,
          origin: {
            ...group,
            transfers: JSON.parse(JSON.stringify(transfers))
          }
        }
      })
    },
    extractTransfers(transfers) {
      return transfers
        .sort((t1, t2) => {
          if (t1.day === t2.day) {
            return t1.sort - t2.sort
          } else {
            return t1.day - t2.day
          }
        })
        .map(t => {
          const fly_no = t.fly_no.trim().toUpperCase()
          const schedules = this.schedules
            ? this.schedules.filter(s => s.fly_no.startsWith(fly_no))
            : []
          const schedule = schedules.find(s => {
            return (
              s.id === t.fly_id &&
              s.effect_date === t.effect_date &&
              s.fly_no === fly_no
            )
          })

          return {
            ...t,
            status: 'update',
            fly_no: fly_no,
            schedules: schedules,
            schedule: schedule,
            estimated_days: schedule && schedule.estimated_days,
            airline: schedule && schedule.airline,
            departure_time: schedule && schedule.departure_time,
            departure_city: schedule && schedule.departure_city,
            arrive_time: schedule && schedule.arrive_time,
            arrive_city: schedule && schedule.arrive_city,
            scheduleValue: schedule && schedule.fly_no
          }
        })
    },
    filteringSchedules() {
      this.items = this.items.map(group => {
        const transfers = this.extractTransfers(group.transfers)

        return {
          ...group,
          transfers: transfers,
          origin: {
            ...group.origin,
            transfers: JSON.parse(JSON.stringify(transfers))
          }
        }
      })
    },
    onAddTransfer(group) {
      group.transfers.push({
        status: 'new',
        id: null,
        day: 1,
        sort: 1,
        direction: '去',
        fly_id: null,
        fly_no: null,
        effect_date: null,
        schedules: this.schedules.slice(0, 100),
        schedule: null,
        scheduleValue: null
      })
      this.modified(group)
    },
    onDeleteTransfer(group, index) {
      if (group.transfers[index].status === 'new') {
        group.transfers.splice(index, 1)
      } else {
        group.transfers[index].status = 'delete'
      }
      this.modified(group)
    },
    onSelectingSchedule: debounce(function(transfer, search) {
      if (search) {
        transfer.schedules = this.schedules.filter(s =>
          s.fly_no.toUpperCase().startsWith(search.toUpperCase().trim())
        )
      } else {
        transfer.schedules = this.schedules.slice(0, 100)
      }
    }, 500),
    onSelectedSchedule(group, index, option) {
      if (option) {
        const transfers = [...group.transfers]
        transfers[index].schedule = option
        transfers[index].fly_id = option.id
        transfers[index].fly_no = option.fly_no
        transfers[index].effect_date = option.effect_date
        transfers[index].estimated_days = option.estimated_days
        transfers[index].airline = option.airline
        transfers[index].departure_time = option.departure_time
        transfers[index].departure_city = option.departure_city
        transfers[index].arrive_time = option.arrive_time
        transfers[index].arrive_city = option.arrive_city

        group.transfers = transfers
        this.modified(group)
      }
    },
    modified(group) {
      group.dirty =
        JSON.stringify(group.transfers) !==
        JSON.stringify(group.origin.transfers)

      this.validate(group)
    },
    restore(group) {
      group.transfers = group.origin.transfers.map(t => ({ ...t }))

      group.dirty = false
      group.error = false
    },
    isDirty() {
      return this.items.filter(x => x.dirty).length > 0
    },
    isEditing() {
      return false
    },
    isValidate() {
      for (const group of this.items) {
        if (!this.validate(group)) {
          return false
        }
      }

      return true
    },
    validate(group) {
      for (const transfer of group.transfers) {
        if (!transfer.fly_no) {
          group.error = '航班必填'
          return false
        }
      }

      group.error = null
      return true
    },
    getData() {
      return this.items
        .filter(group => group.dirty)
        .map(group => ({
          code: group.code,
          transfers: group.transfers.map(t => {
            return {
              status: t.status,
              id: t.id,
              day: t.day,
              sort: t.sort,
              direction: t.direction,
              fly_id: t.fly_id,
              fly_no: t.fly_no,
              effect_date: t.effect_date
            }
          })
        }))
    },
    rowClass(group, index) {
      if (group.error) {
        return 'has-background-danger-light'
      } else if (group.dirty) {
        return 'has-background-warning'
      } else {
        return index % 2 === 1 ? 'has-background-info-light' : ''
      }
    },
    transferRowClass(group, index, transfer) {
      let rowClass = this.rowClass(group, index)

      if (transfer.status === 'delete') {
        rowClass = 'is-hidden ' + rowClass
      }

      return rowClass.trim()
    }
  }
}
</script>

<style lang="scss">
#batch-transfer-table {
  .b-table {
    .table-wrapper.has-sticky-header tr:first-child th {
      z-index: 10;
    }

    & > .table-wrapper {
      & > .table {
        min-width: 1260px;

        .align-right {
          text-align: right;
        }

        .transfer-table {
          .table-wrapper tr:first-child th {
            z-index: 2;
          }

          .field.is-floating-label .label:before {
            background-color: transparent;
          }

          .table {
            border: none;

            td,
            th {
              border: none;
            }
          }

          [readonly].input {
            cursor: not-allowed;
            background-color: whitesmoke;
            box-shadow: none;
            color: #7a7a7a;
          }
        }
      }
    }
  }

  .schedule-autocomplete {
    .dropdown-content {
      width: fit-content;
      overflow-y: auto;
    }
  }

  .fly-schedule-media {
    .media-left {
      margin-right: 10px;
      max-width: 100px;
      min-width: 100px;
      width: 100px;
      overflow: scroll;
    }

    .media-content {
      max-width: 160px;
      min-width: 160px;
      width: 160px;
    }

    .media-right {
      margin-left: 10px;
      max-width: 250px;
      min-width: 250px;
      width: 250px;

      .schedule-table {
        background: transparent;

        th,
        td {
          text-align: center !important;
          vertical-align: middle !important;
          border: none;
          padding: 0 2px;

          .week-day {
            width: 14px;
            height: 14px;
            border-radius: 50%;
            font-size: 12px;
            color: #fff;
            line-height: 14px;
            text-align: center;
            background-color: #cccccc;
            padding: 2px;

            &.week-check {
              background-color: lightseagreen;
            }
          }
        }
      }
    }
  }
}
</style>
